import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, Calendar, Clock, MapPin, Users, Plus, FileText,
  CheckCircle, AlertTriangle, Target, BookOpen, Zap,
  Sparkles, Lightbulb,
  Brain, Navigation, Presentation
} from 'lucide-react';
import { 
  toutesLesReunionsAdminRH, 
  genererResumeIA,
  type Reunion, 
  type OrdreDuJourItem,
  type Action
} from '../data/adminRHReunionsData';

interface AdminRHReunionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminRHReunionsModal: React.FC<AdminRHReunionsModalProps> = ({ isOpen, onClose }) => {
  const [vueActive, setVueActive] = useState<'liste' | 'details'>('liste');
  const [reunionSelectionnee, setReunionSelectionnee] = useState<Reunion | null>(null);
  const [ongletActif, setOngletActif] = useState<'info' | 'agenda' | 'notes' | 'actions' | 'resume'>('info');
  const [activeODJItem, setActiveODJItem] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);

  // Fonction pour obtenir l'avatar d'un responsable
  const getResponsableAvatar = (nomComplet: string): string | null => {
    const nomLower = nomComplet.toLowerCase();
    if (nomLower.includes('françois') || nomLower.includes('delisle')) return '/FP.png';
    if (nomLower.includes('mélissa') || nomLower.includes('pelletier')) return '/MP.png';
    if (nomLower.includes('émilie') || nomLower.includes('quintal')) return '/EQ.png';
    if (nomLower.includes('richard')) return '/RD.png';
    if (nomLower.includes('yannick')) return '/DB.png';
    if (nomLower.includes('dany') || nomLower.includes('bourassa')) return '/DB.png';
    return null;
  };

  if (!isOpen) return null;

  const handleOuvrirReunion = (reunion: Reunion) => {
    setReunionSelectionnee(reunion);
    setVueActive('details');
    setOngletActif('info');
    setActiveODJItem(reunion.ordreDuJour[0]?.id || null);
  };

  const handleRetourListe = () => {
    setVueActive('liste');
    setReunionSelectionnee(null);
  };

  const handleGenererAvecIA = async () => {
    if (!reunionSelectionnee) return;
    
    setIsGeneratingIA(true);
    
    // Simuler l'IA qui travaille (animation)
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Générer le résumé
    const resumeGenere = genererResumeIA(reunionSelectionnee);
    
    // Mettre à jour la réunion
    reunionSelectionnee.resumeGenere = true;
    reunionSelectionnee.resumeContenu = resumeGenere;
    
    setIsGeneratingIA(false);
    
    // Rafraîchir l'affichage
    setReunionSelectionnee({ ...reunionSelectionnee });
  };


  const getStatutBadge = (statut: Reunion['statut']) => {
    const config = {
      'Planifiée': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Planifiée' },
      'En cours': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours' },
      'Complétée': { bg: 'bg-green-100', text: 'text-green-700', label: 'Complétée' }
    };
    const { bg, text, label } = config[statut];
    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-xs font-semibold`}>{label}</span>;
  };

  const getStatutODJBadge = (statut: OrdreDuJourItem['statut']) => {
    const config = {
      'Non traité': { bg: 'bg-gray-100', text: 'text-gray-700' },
      'En discussion': { bg: 'bg-blue-500', text: 'text-white' },
      'Complété': { bg: 'bg-green-500', text: 'text-white' },
      'Reporté': { bg: 'bg-orange-500', text: 'text-white' }
    };
    const { bg, text } = config[statut];
    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-xs font-semibold`}>{statut}</span>;
  };

  const getStatutActionBadge = (statut: Action['statut']) => {
    const config = {
      'À faire': { bg: 'bg-gray-100', text: 'text-gray-700' },
      'En cours': { bg: 'bg-blue-500', text: 'text-white' },
      'Complété': { bg: 'bg-green-500', text: 'text-white' },
      'Bloqué': { bg: 'bg-red-500', text: 'text-white' }
    };
    const { bg, text } = config[statut];
    return <span className={`${bg} ${text} px-2 py-1 rounded text-xs font-medium`}>{statut}</span>;
  };

  const getPrioriteBadge = (priorite: Action['priorite']) => {
    const config = {
      'Basse': { bg: 'bg-gray-400', text: 'text-white' },
      'Moyenne': { bg: 'bg-blue-500', text: 'text-white' },
      'Haute': { bg: 'bg-orange-500', text: 'text-white' },
      'Critique': { bg: 'bg-red-600', text: 'text-white' }
    };
    const { bg, text } = config[priorite];
    return <span className={`${bg} ${text} px-2 py-1 rounded text-xs font-bold`}>{priorite}</span>;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 p-6">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-xl p-4 rounded-2xl">
                <Navigation className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-3xl font-black text-white">
                    {vueActive === 'liste' ? '🧭 Réunions de gouvernance – Administration & RH' : reunionSelectionnee?.titre}
                  </h1>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-white/90 text-lg">
                  {vueActive === 'liste' 
                    ? `${toutesLesReunionsAdminRH.length} réunion(s) enregistrée(s)` 
                    : `Réunion du ${reunionSelectionnee ? new Date(reunionSelectionnee.date).toLocaleDateString('fr-FR') : ''}`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* VUE LISTE */}
          {vueActive === 'liste' && (
            <div className="flex-1 overflow-y-auto p-8">
              <div className="flex justify-end mb-6">
                <button className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all shadow-lg">
                  <Plus className="w-5 h-5" />
                  Nouvelle Réunion
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {toutesLesReunionsAdminRH.map((reunion) => (
                  <motion.div
                    key={reunion.id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-white border-2 border-gray-200 rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all"
                    onClick={() => handleOuvrirReunion(reunion)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="text-sm text-gray-500">
                        {new Date(reunion.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      {getStatutBadge(reunion.statut)}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4">{reunion.titre}</h3>

                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {reunion.heure} ({reunion.dureeMinutes} min)
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {reunion.lieu}
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {reunion.objectifPrincipal.substring(0, 60)}...
                      </div>
                    </div>

                    <div className="flex justify-between text-xs text-gray-500 pt-4 border-t">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span className={reunion.participants.filter(p => p.present).length > 0 ? 'text-teal-600 font-semibold' : ''}>
                          {reunion.participants.filter(p => p.present).length} participant{reunion.participants.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        <span className={reunion.notes.length > 0 ? 'text-orange-600 font-semibold' : ''}>
                          {reunion.notes.length} note{reunion.notes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span className={reunion.ordreDuJour.length > 0 ? 'text-purple-600 font-semibold' : ''}>
                          {reunion.ordreDuJour.length} point{reunion.ordreDuJour.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        <span className={reunion.actions.length > 0 ? 'text-red-600 font-semibold' : ''}>
                          {reunion.actions.length} action{reunion.actions.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {reunion.resumeGenere && (
                      <div className="mt-4 flex items-center gap-2 text-green-600 text-sm font-semibold">
                        <Sparkles className="w-4 h-4" />
                        Résumé complété
                      </div>
                    )}
                    {!reunion.resumeGenere && reunion.statut === 'Complétée' && (
                      <div className="mt-4 text-gray-400 text-sm">
                        Résumé non généré
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* VUE DÉTAILS */}
          {vueActive === 'details' && reunionSelectionnee && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Navigation retour + onglets */}
              <div className="border-b border-gray-200 bg-white px-6 py-3">
                <button
                  onClick={handleRetourListe}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour à la liste
                </button>

                <div className="flex gap-2">
                  {[
                    { id: 'info', label: 'Info & Participants', icon: Users },
                    { id: 'agenda', label: 'Ordre du Jour', icon: FileText },
                    { id: 'notes', label: 'Notes/Décisions/Enjeux', icon: BookOpen },
                    { id: 'actions', label: 'Actions/TODOS', icon: CheckCircle },
                    { id: 'resume', label: 'Résumé', icon: Zap }
                  ].map((onglet) => {
                    const Icon = onglet.icon;
                    return (
                      <button
                        key={onglet.id}
                        onClick={() => setOngletActif(onglet.id as any)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-medium ${
                          ongletActif === onglet.id
                            ? 'bg-teal-600 text-white shadow-lg'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {onglet.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contenu des onglets */}
              <div className={`flex-1 min-h-0 ${ongletActif === 'agenda' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto p-8'}`}>
                {/* ONGLET INFO & PARTICIPANTS */}
                {ongletActif === 'info' && (
                  <div className="space-y-6">
                    {/* Informations réunion */}
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Calendar className="w-6 h-6 text-teal-600" />
                          Informations de la Réunion
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Nom de la réunion</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.titre}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Date</div>
                          <div className="font-semibold text-gray-900">
                            {new Date(reunionSelectionnee.date).toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Heure</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.heure}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Durée</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.dureeMinutes} minutes</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Lieu</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.lieu}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Statut</div>
                          <div>{getStatutBadge(reunionSelectionnee.statut)}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-500 mb-1">Objectif principal</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.objectifPrincipal}</div>
                        </div>
                      </div>
                    </div>

                    {/* Participants */}
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Users className="w-6 h-6 text-teal-600" />
                          Participants ({reunionSelectionnee.participants.filter(p => p.present).length})
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {reunionSelectionnee.participants.map((participant) => (
                          <div key={participant.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-2">
                              {participant.avatar && (
                                <img 
                                  src={participant.avatar} 
                                  alt={`${participant.prenom} ${participant.nom}`}
                                  className="w-10 h-10 rounded-full"
                                />
                              )}
                              <div>
                                <div className="font-semibold text-gray-900">{participant.prenom} {participant.nom}</div>
                                <div className="text-sm text-gray-500">{participant.role}</div>
                              </div>
                            </div>
                            <div className="text-sm text-teal-600 mb-3">{participant.email}</div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-gray-600">Statut:</span>
                              <span className={participant.present ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                                {participant.present ? '✓ Présent' : 'Absent'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ONGLET ORDRE DU JOUR */}
                {ongletActif === 'agenda' && (() => {
                  // S'assurer qu'un point est toujours sélectionné
                  if (!activeODJItem && reunionSelectionnee.ordreDuJour.length > 0) {
                    setActiveODJItem(reunionSelectionnee.ordreDuJour[0].id);
                  }
                  
                  return (
                    <div className="flex-1 flex overflow-hidden min-h-0">
                      {/* Sidebar - Liste des points de l'ordre du jour */}
                      <div className="w-96 border-r border-gray-200 bg-gray-50 flex flex-col min-w-0">
                        <div className="p-6 pb-4 flex-shrink-0 border-b border-gray-200">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Presentation className="w-6 h-6 text-teal-600" />
                            Ordre du Jour
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({reunionSelectionnee.ordreDuJour.length} points)
                            </span>
                          </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 pb-6" style={{ minHeight: 0 }}>
                          <div className="space-y-2">
                            {reunionSelectionnee.ordreDuJour.map((item) => {
                            const avatar = getResponsableAvatar(item.responsable);
                            return (
                              <button
                                key={item.id}
                                onClick={() => setActiveODJItem(item.id)}
                                className={`w-full text-left p-4 rounded-xl transition-all ${
                                  activeODJItem === item.id
                                    ? 'bg-teal-600 text-white shadow-lg'
                                    : 'bg-white hover:bg-gray-100 text-gray-900 border border-gray-200'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-3">
                                    <span className={`text-xl font-bold ${
                                      activeODJItem === item.id ? 'text-white' : 'text-teal-600'
                                    }`}>
                                      {item.numero}
                                    </span>
                                    <div className="flex-1">
                                      <span className="font-semibold text-base block">{item.titre}</span>
                                      <div className="flex items-center gap-2 mt-1">
                                        {avatar && (
                                          <img 
                                            src={avatar} 
                                            alt={item.responsable}
                                            className="w-5 h-5 rounded-full border border-gray-300"
                                          />
                                        )}
                                        <span className={`text-xs ${
                                          activeODJItem === item.id ? 'text-white/80' : 'text-gray-500'
                                        }`}>
                                          {item.responsable}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {activeODJItem === item.id ? (
                                    <span className="text-xs px-3 py-1 rounded-full font-semibold bg-white/20 text-white">
                                      {item.statut}
                                    </span>
                                  ) : (
                                    getStatutODJBadge(item.statut)
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`text-xs ${
                                    activeODJItem === item.id ? 'text-white/80' : 'text-gray-500'
                                  }`}>
                                    {item.dureeMinutes} min
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                          </div>
                        </div>
                      </div>

                      {/* Main Content - Détail du point sélectionné */}
                      <div className="flex-1 overflow-y-auto bg-gray-50">
                        {(() => {
                          const itemActif = reunionSelectionnee.ordreDuJour.find(item => item.id === activeODJItem) || reunionSelectionnee.ordreDuJour[0];
                          if (!itemActif) {
                            return (
                              <div className="p-8 flex items-center justify-center h-full">
                                <p className="text-gray-500">Aucun point d'ordre du jour disponible</p>
                              </div>
                            );
                          }
                          
                          const avatar = getResponsableAvatar(itemActif.responsable);
                          
                          return (
                            <div className="p-8">
                              {/* Header du point */}
                              <div className="bg-white rounded-xl p-8 shadow-md mb-8 border-l-4 border-teal-500">
                                <div className="flex items-start justify-between mb-6">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-4 mb-3">
                                      <span className="bg-teal-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-bold text-2xl shadow-lg">
                                        {itemActif.numero}
                                      </span>
                                      <div className="flex-1">
                                        <h3 className="text-3xl font-bold text-gray-900 mb-2">{itemActif.titre}</h3>
                                        <div className="flex items-center gap-4">
                                          {avatar && (
                                            <div className="flex items-center gap-2">
                                              <img 
                                                src={avatar} 
                                                alt={itemActif.responsable}
                                                className="w-8 h-8 rounded-full border-2 border-teal-200"
                                              />
                                              <span className="text-base font-semibold text-gray-700">
                                                {itemActif.responsable}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <p className="text-gray-700 mb-6 text-lg leading-relaxed">{itemActif.description}</p>
                                    <div className="flex items-center gap-4">
                                      <span className="px-4 py-2 rounded-full text-base font-semibold bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
                                        Durée: <strong>{itemActif.dureeMinutes} min</strong>
                                      </span>
                                      {getStatutODJBadge(itemActif.statut)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Contenu détaillé */}
                              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-teal-500">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <span className="bg-teal-100 text-teal-700 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg">
                                        1
                                      </span>
                                      <h5 className="text-2xl font-bold text-gray-900">Détails du point</h5>
                                    </div>
                                    <div className="text-gray-700 leading-relaxed text-base space-y-4 ml-14">
                                      <div className="space-y-3">
                                        <p className="leading-relaxed">{itemActif.description}</p>
                                        <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
                                          <p className="font-semibold text-gray-900 mb-2">Responsable :</p>
                                          <div className="flex items-center gap-2">
                                            {avatar && (
                                              <img 
                                                src={avatar} 
                                                alt={itemActif.responsable}
                                                className="w-6 h-6 rounded-full"
                                              />
                                            )}
                                            <span className="text-gray-700">{itemActif.responsable}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-2 ml-4">
                                    <span className="text-xs px-3 py-1 rounded-full font-semibold bg-teal-100 text-teal-700">
                                      {itemActif.dureeMinutes} min
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}

                {/* ONGLET NOTES/DÉCISIONS/ENJEUX */}
                {ongletActif === 'notes' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <BookOpen className="w-6 h-6 text-teal-600" />
                          Notes de Réunion
                        </h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowGuide(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <Lightbulb className="w-4 h-4" />
                            Guide
                          </button>
                        </div>
                      </div>

                      {/* Notes Générales */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'generale').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                            Notes Générales ({reunionSelectionnee.notes.filter(n => n.type === 'generale').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'generale').map((note) => (
                              <div key={note.id} className="border-l-4 border-blue-500 bg-blue-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Décisions */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'decision').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Décisions ({reunionSelectionnee.notes.filter(n => n.type === 'decision').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'decision').map((note) => (
                              <div key={note.id} className="border-l-4 border-green-500 bg-green-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Enjeux */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'enjeu').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                            Enjeux ({reunionSelectionnee.notes.filter(n => n.type === 'enjeu').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'enjeu').map((note) => (
                              <div key={note.id} className="border-l-4 border-orange-500 bg-orange-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risques */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'risque').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Target className="w-5 h-5 text-red-600" />
                            Risques ({reunionSelectionnee.notes.filter(n => n.type === 'risque').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'risque').map((note) => (
                              <div key={note.id} className="border-l-4 border-red-500 bg-red-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ONGLET ACTIONS/TODOS */}
                {ongletActif === 'actions' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <CheckCircle className="w-6 h-6 text-teal-600" />
                            Actions & TODOS
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length}/{reunionSelectionnee.actions.length} complétées • {Math.round((reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length / (reunionSelectionnee.actions.length || 1)) * 100)}% de progression
                          </p>
                        </div>
                      </div>

                      {/* Barre de progression */}
                      {reunionSelectionnee.actions.length > 0 && (
                        <div className="mb-6">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-teal-600 to-emerald-600 h-3 rounded-full transition-all"
                              style={{ 
                                width: `${(reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length / reunionSelectionnee.actions.length) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Grouper par statut */}
                      {['En cours', 'À faire', 'Bloqué', 'Complété'].map((statut) => {
                        const actions = reunionSelectionnee.actions.filter(a => a.statut === statut);
                        if (actions.length === 0) return null;

                        const statutConfig = {
                          'En cours': { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700' },
                          'À faire': { bg: 'bg-gray-50', border: 'border-gray-400', title: 'text-gray-700' },
                          'Bloqué': { bg: 'bg-red-50', border: 'border-red-500', title: 'text-red-700' },
                          'Complété': { bg: 'bg-green-50', border: 'border-green-500', title: 'text-green-700' }
                        };

                        const config = statutConfig[statut as keyof typeof statutConfig];

                        return (
                          <div key={statut} className="mb-6">
                            <h4 className={`font-bold mb-3 flex items-center gap-2 ${config.title}`}>
                              {statut}
                              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">{actions.length}</span>
                            </h4>
                            <div className="space-y-3">
                              {actions.map((action) => (
                                <div key={action.id} className={`border-l-4 ${config.border} ${config.bg} rounded-lg p-4`}>
                                  <div className="flex justify-between items-start mb-2">
                                    <h5 className="font-bold text-gray-900">{action.titre}</h5>
                                    <div className="flex gap-2">
                                      {getPrioriteBadge(action.priorite)}
                                      {getStatutActionBadge(action.statut)}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-3">{action.description}</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-4 h-4" />
                                      {action.responsable}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      Échéance: {new Date(action.echeance).toLocaleDateString('fr-FR')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ONGLET RÉSUMÉ */}
                {ongletActif === 'resume' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Sparkles className="w-6 h-6 text-teal-600" />
                          Résumé de la Réunion
                        </h3>
                        <div className="flex gap-2">
                          {!reunionSelectionnee.resumeGenere && (
                            <button
                              onClick={handleGenererAvecIA}
                              disabled={isGeneratingIA || reunionSelectionnee.notes.length === 0}
                              className={`flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg transition-all shadow-lg ${
                                isGeneratingIA || reunionSelectionnee.notes.length === 0
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:shadow-xl hover:scale-105'
                              }`}
                            >
                              {isGeneratingIA ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                  Génération...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Générer avec IA
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {reunionSelectionnee.resumeGenere && reunionSelectionnee.resumeContenu && (
                        <div className="prose max-w-none">
                          <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                            {reunionSelectionnee.resumeContenu}
                          </div>
                        </div>
                      )}

                      {!reunionSelectionnee.resumeGenere && (
                        <div className="text-center py-8 text-gray-500">
                          <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                          <p className="mb-4">Aucun résumé généré pour cette réunion</p>
                          <p className="text-sm">Cliquez sur "Générer avec IA" pour créer un résumé professionnel</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GUIDE BONNES PRATIQUES MODAL */}
          {showGuide && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGuide(false)}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <Lightbulb className="w-7 h-7 text-teal-600" />
                    Guide des bonnes pratiques
                  </h3>
                  <button onClick={() => setShowGuide(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <p className="text-gray-600 mb-6">Comment documenter efficacement vos réunions de gouvernance</p>

                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Notes Générales
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Ce qui se dit pendant la réunion:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Discussions importantes et contexte</li>
                      <li>• Points de vue exprimés par les participants</li>
                      <li>• Informations partagées et explications</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Décisions
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Ce qui est approuvé ou choisi:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        Ex: "Nous approuvons le budget de 15k$ pour le processus recevables"
                      </li>
                      <li className="font-semibold text-green-700">Important: Toujours noter QUI décide et QUAND</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      Enjeux
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Défis et contraintes à considérer:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Contraintes budgétaires ou de ressources</li>
                      <li>• Dépendances bloquantes ou problèmes opérationnels</li>
                      <li>• Points nécessitant une résolution rapide</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowGuide(false)}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Compris!
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AdminRHReunionsModal;
